import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries.ts";
import { MapView, type MapFocusRequest } from "@/map";
import { CATEGORY_SCOPE, type MapCategory } from "@/map/config/categories";
import { AlertStatus } from "@/alerts/types/machine-alert";
import type { MachineEvent, TruckEvent } from "@/machines/types/event";
import type { Alert } from "@/alerts/types/machine-alert";
import { useGetAlertById } from "@/alerts/api/queries/machine-alerts.queries.ts";
import { getAlertById } from "@/alerts/api/requests/machine-alerts.requests.ts";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { QUERY_KEYS as MACHINE_QUERY_KEYS } from "@/machines/api/config.ts";
import { useGetGps } from "@/machines/api/queries/gps.queries.ts";
import { useMachineDetailMapCoordinates } from "@/machines/components/hooks/use-machine-detail-map-coordinates";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useMapPageData } from "@/map/hooks/useMapPageData";
import { useMapAreaHeight } from "@/map/hooks/useMapAreaHeight";
import { useMapBreadcrumbs } from "@/map/hooks/useMapBreadcrumbs";
import { useMapMarkers } from "@/map/hooks/useMapMarkers";
import { useAlertCarousel } from "@/map/hooks/useAlertCarousel";
import { useSidebarCollapse } from "@/map/hooks/useSidebarCollapse";
import { useMapPanels } from "@/map/hooks/useMapPanels";
import { useMapSelection, defaultMachineIdAt } from "@/map/hooks/useMapSelection";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import { useMapLiveSession } from "@/map/hooks/useMapLiveSession";
import { alertFitsLiveWindow, liveLeadMs, shouldResyncOnReturn } from "@/map/hooks/useRealtimeWindow";
import { useMapShortcuts } from "@/map/hooks/useMapShortcuts";
import { useLocationAlerts, type LocationAlertFilters } from "@/alerts/hooks/useLocationAlerts";
import { useLocationEvents, type MapEventRow } from "@/map/hooks/useLocationEvents";
import { useNewAlerts } from "@/alerts/hooks/useNewAlerts";
import { useTimelineEditQueueStore } from "@/machines/stores/useTimelineEditQueueStore";
import { useAlertNotifyStore } from "@/alerts/stores/use-alert-notify-store";
import {
  buildAlertNavList,
  navAnchorIndex,
  pickNewestNewAlert,
  type AlertFocusTarget,
} from "@/map/utils/new-alert-target";
import { parseSelectionId, alertSelectionId } from "@/map/utils/map-coordinates";
import MachineRail from "@/map/components/map-preview/MachineRail";
import CategoryRail from "@/map/components/map-preview/CategoryRail";
import CategoryPanel from "@/map/components/map-preview/CategoryPanel";
import MapEventCard from "@/map/components/map-preview/MapEventCard";
import { useAlertVerdict } from "@/alerts/hooks/useAlertVerdict";
import { useAlertWorkspaceDetail } from "@/alerts/api/queries/alerts-workspace.queries";
import type { AlertVerdict } from "@/alerts/utils/alert-decision";
import AlertReviewV2 from "@/map/components/map-preview/AlertReviewV2";
import MapMachineEventCard from "@/map/components/map-preview/MapMachineEventCard";
import MapTimelineStack, { TIMELINE_POLL_MS } from "@/map/components/map-preview/MapTimelineStack";
import MapTimelineRow from "@/map/components/map-preview/MapTimelineRow";
import MapVideoPanel from "@/map/components/map-preview/MapVideoPanel";
import type { AvailableTrack } from "@/machines/components/common/timeline-editor/main-preview/main-preview";
import MachineTimelineProvider from "@/map/components/map-preview/MachineTimelineProvider";
import LiveTimelineDomainProvider from "@/map/components/map-preview/LiveTimelineDomainProvider";
import {
  useMapTimelineVisibilityStore,
  visibleTimelineMachines,
} from "@/map/stores/useMapTimelineVisibilityStore";
import MapModeBar, { type MapMode } from "@/map/components/map-preview/MapModeBar";
import MapShortcutsHelp from "@/map/components/map-preview/MapShortcutsHelp";
import { Button } from "@/ui/components/ui/button";
import { ChevronUp, AlertCircle } from "lucide-react";

/** Stable empty list, so a gated per-machine query set doesn't churn per render. */


/** Stable empty list so a not-yet-loaded location query doesn't churn selection. */
const EMPTY_LOCATIONS: never[] = [];


/**
 * `/map` aggregate page — org-wide, map-first. Live data via `useMapPageData`
 * (+ location-aggregate alerts/events); map rendered by the shared `MapView`.
 * GPS/video/images panels + overlay polygons are wired in Phase 3.
 */
const MapPreviewPage = () => {
  const { t } = useTranslation("map");
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  // Event selected in the Events panel — opens the on-map event detail card
  // (shares the top-right slot with the alert card; selecting one clears the other).
  const [selectedEvent, setSelectedEvent] = useState<MapEventRow | null>(null);
  // Alert hovered in the feed or on a map pin. The emphasized alert (hovered,
  // else selected) stays fully opaque everywhere while the rest dim to half.
  const [hoveredAlertId, setHoveredAlertId] = useState<string | null>(null);
  const emphasizedAlertId = hoveredAlertId ?? selectedAlertId;
  // Global alert status filter (multi-select) — drives both the Alerts feed AND
  // the on-map alert markers. Empty selection means no status constraint (all
  // statuses shown everywhere).
  const [alertStatusFilter, setAlertStatusFilter] = useState<AlertStatus[]>([]);
  // Server-side alert filters (type/label/confidence) — like the status filter,
  // they drive the feed, the on-map alert markers, and the notify flow.
  const [alertFilters, setAlertFilters] = useState<LocationAlertFilters>({});
  // Bottom timeline — only relevant while a machine is selected (auto-collapses on deselect).
  // Track list of the ACTIVE machine's timeline (reported by its stack row) —
  // feeds the stack header's Track-Settings modal.
  const [availableTracks, setAvailableTracks] = useState<AvailableTrack[]>([]);
  // Timeline window mode — "realtime" swaps the global date range for a
  // rolling live window (timeline + synced video).
  // Visible span of the realtime view (user-selectable: 5m / 10m / 30m / 1h).
  // Newest event of the live rolling window (reported by the timeline panel) —
  // the on-map event card follows it so the detail auto-updates while live.
  // Realtime "stay live": whether the shared cursor + primary video are pinned
  // to the newest moment. Armed on entering realtime / switching machine; the
  // user leaves it by scrubbing back or pausing (reported by the timeline
  // provider's LiveEdgeDriver), and re-arms it with the "Go live" control.
  // Newest AVAILABLE footage edge for the active primary video (start = playable
  // resume point, end = the true live edge), reported by the primary tile. The
  // live cursor is pinned to this — where clips actually exist — NOT to the event
  // edge, which runs minutes ahead of uploaded footage. Null until footage loads.
  // Bumped (to Date.now()) on each "Go live" click — the primary video resets to
  // the live edge on its change, instead of crawling forward from a reviewed alert.
  // Timeline-synced video: shown in the right "Video" drawer, or popped out to a
  // draggable floating window (placeable anywhere).
  // Timestamp the timeline + video should snap to (set by an Alert-preview click).
  // Map settings (relocated from the floating on-map controls into the right Settings panel).
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayVersion, setOverlayVersion] = useState<string | undefined>(undefined);
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<number[]>([]);
  // Map mode — "alert-preview" turns on alert automation (polling + new badge).
  const [mapMode, setMapMode] = useState<MapMode>("alert-preview");
  // Keyboard-shortcuts help overlay (toggled with `?`).
  // Alert-card image carousel state, lifted here (from MapEventCard) so the
  // capture-phase keyboard hook can route arrows/Esc on `lightboxOpen`. The
  // index is the single source of truth shared by the carousel and lightbox.
  // Camera-focus intent handed to MapView. Selecting an alert sets this so the
  // map moves to the alert; the incrementing nonce lets a repeat selection of
  // the same alert (same coords) re-fire the move.
  const [focusRequest, setFocusRequest] = useState<MapFocusRequest | null>(null);
  const focusNonceRef = useRef(0);
  // The live cursor's current value, mirrored by the shell provider. Read only on
  // tab return, to tell a stalled playback position from a healthy one.
  const liveCursorRef = useRef<number | null>(null);
  // Bridges the memoized timeline-segment handler to the latest `focusAlert`
  // (defined below) without destabilizing its identity — see handleTimelineEventOpen.
  const focusAlertRef = useRef<
    (
      alert: AlertFocusTarget,
      focus: "hard" | "soft" | "none" | "max",
      acknowledgeAlarm?: boolean,
    ) => void
  >(() => {});
  // Same bridge for the breadcrumb's location dropdown → `handleSelectLocation`
  // (defined below). Identity MUST stay stable: `useSetBreadcrumbs` diffs its
  // entries with JSON.stringify, which drops functions, so a callback that
  // changes identity is never re-published and the crumb would keep calling the
  // first render's closure.
  const selectLocationRef = useRef<(id: number) => void>(() => {});
  const handleBreadcrumbSelectLocation = useCallback((id: number) => {
    selectLocationRef.current(id);
  }, []);

  const queryClient = useQueryClient();
  const { selectedOrganizationId } = useOrganizationStore();

  // Selection drives useMapPageData below, so it has to resolve first. It reads
  // the location list from the same query (shared cache, no extra request).
  const locationsForSelection = useGetAllLocations().data?.data ?? EMPTY_LOCATIONS;
  const {
    selectedLocationId, setSelectedLocationId,
    selectedMachineId, setSelectedMachineId,
    view, setView,
  } = useMapSelection({
    locations: locationsForSelection,
    organizationId: selectedOrganizationId,
    // Everything below belongs to the previous org — see the hook's docs.
    onOrgChange: () => {
      setSelectedAlertId(null);
      setSelectedEvent(null);
      setAlertSeekTime(null);
      setAvailableTracks([]);
      setOverlayVersion(undefined);
      setSelectedPolygonIds([]);
      lastAutoLoadedRef.current = null;
    },
  });

  const {
    locations,
    selectedLocation,
    machines,
    selectedMachine,
    kpisByDevice,
    isLoadingLocations,
    isLoadingMachines,
    isError,
  } = useMapPageData(selectedLocationId, selectedMachineId);

  /**
   * Alert automation — 30s polling, the new-alerts badge, click-to-focus. BOTH
   * alert modes want it; they differ only in what the review looks like.
   */
  const alertPreview = mapMode === "alert-preview" || mapMode === "alert-review-v2";
  /**
   * V2 swaps the floating card for the on-map review overlay. The card is still
   * the surface in "Alert · review", and still owns pin, collapse and the
   * lightbox — which is why both modes exist rather than one replacing the other.
   */
  const reviewV2 = mapMode === "alert-review-v2";
  const {
    openCategory, setOpenCategory,
    categoryFloating, setCategoryFloating,
    timelineOpen, setTimelineOpen,
    timelineFloating, setTimelineFloating,
    videoFloating, setVideoFloating,
    modeBarCollapsed, setModeBarCollapsed,
    showShortcuts, setShowShortcuts,
    videoAlwaysDocked, videoOpen, videoReviewDock,
    videoDockedLeft, videoDockedRight,
    popOutVideo: handleVideoPopOut,
    dockVideo: handleVideoDock,
    closeVideo: handleVideoClose,
  } = useMapPanels({ alertPreview, hasMachine: !!selectedMachine });

  const {
    timelineMode, setTimelineMode,
    liveSpanMs, setLiveSpanMs,
    realtimeWindow,
    setLiveLatestEvent,
    setLiveFollowPaused,
    cursorLiveFollow, setCursorLiveFollow,
    goLiveTime,
    alertSeekTime, setAlertSeekTime,
    liveEdge, liveFootageStart, reviewTime, setReviewing,
    handleGoLive, handleLeaveLive, handleFootageEdgeChange,
  } = useMapLiveSession({
    selectedMachineId,
    selectedAlertId,
    onGoLive: () => setSelectedAlertId(null),
  });

  // Organization crumb — a switcher (dropdown) that swaps the selected org, just
  // like the dashboard pages. Changing it drives `useMapPageData` via the org
  // store (the locations query is keyed on `selectedOrganizationId`).
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);

  useMapBreadcrumbs({
    organizationName: orgData?.data?.name,
    organizationId: selectedOrganizationId,
    isOrgLoading,
    location: selectedLocation,
    showLocation: view === "location",
    machine: selectedMachine,
    onSelectLocation: handleBreadcrumbSelectLocation,
  });

  // The globally selected from→to window. Every alert surface on this page is
  // bounded by it, and it is part of the notify scope below.
  const { value } = useDateTimeRangeStore();

  // The Firestore live channel is NOT mounted here any more — `DashboardLayout`
  // owns the app's single subscription (see `use-alerts-live.ts`). This page
  // still gets everything it did: hints land in the shared live store, and
  // `useLocationAlerts` below already re-reads the feed off them, so markers,
  // the alert card and prev/next keep seeing live arrivals. Polling is likewise
  // untouched, so a dead channel still leaves the map working on its 10s poll.
  //
  // The map alert feed (markers + right-side feed + chime source) uses ONE
  // window per mode — the same window the timeline rows use.
  //
  // In RANGE mode that is the global picker, strictly: a past `to` is a review
  // window and must not leak alerts from outside it.
  //
  // In LIVE mode it is the rolling realtime window. It used to stay on the
  // picker even here, and the result was the map going blank while the timeline
  // directly above it showed fresh alerts — the two were reading different
  // windows. That happens whenever the picker's range has ended, which is easy
  // to hit: shifts resolve against the LOCATION's timezone, and the backend
  // currently returns `timezone: null` for every location, so they fall back to
  // the viewer's browser zone and can sit hours away from the site's real shift.
  // Following the live window in live mode makes the map agree with the timeline
  // regardless, and keeps the chime alive when the picker drifts.
  const { alerts: locationAlerts, machineNameByDevice, isLoading: alertsLoading } =
    useLocationAlerts(
      machines,
      alertPreview ? 10000 : undefined,
      alertFilters,
      timelineMode === "realtime" ? realtimeWindow : undefined,
    );
  // The Events panel is the only consumer of `locationEvents` (the selected
  // event lives in its own state; no marker path reads the feed), so fetch it
  // only while that panel is open — otherwise we skip the N×EVENTS_PER_MACHINE fetch.
  const { events: locationEvents, isLoading: eventsLoading } = useLocationEvents(
    machines,
    openCategory === "events",
  );

  // New-alerts badge (Alert-preview mode). Reset the baseline when the Alerts
  // list opens; hidden while it's open.
  const { newCount: rawNewAlerts, markSeen } = useNewAlerts(locationAlerts, alertPreview);
  useEffect(() => {
    if (openCategory === "alerts") markSeen();
  }, [openCategory, markSeen]);
  const newAlertsCount = openCategory === "alerts" ? 0 : rawNewAlerts;

  // Whether an ARRIVING alert may take the operator off what they are doing.
  // Off is the "I am validating previous days" switch — see the store's field.
  const followNewAlerts = useAlertNotifyStore((s) => s.followNewAlerts);
  const setFollowNewAlerts = useAlertNotifyStore((s) => s.setFollowNewAlerts);
  const markAlertHandled = useAlertNotifyStore((s) => s.markHandled);
  const markAllAlertsSeen = useAlertNotifyStore((s) => s.markAllSeen);
  const acknowledgeChime = useAlertNotifyStore((s) => s.acknowledgeChime);
  // The bell is no longer fed from here. It used to be pushed this History feed,
  // which is bounded by the global date picker — so the operator's review window
  // silently decided what could notify them, and the bell emptied the moment they
  // left /map. `DashboardLayout` now feeds it from the Queue instead
  // (`use-alert-notify-feed.ts`). The map still READS the store below for its
  // "new" highlight and the handled/seen actions.

  // GPS trail — fetched only while the GPS panel is open (empty device_mxid disables it).
  const gpsPanelOpen = openCategory === "gps" && !!selectedMachine;
  const { data: gpsData, status: gpsStatus } = useGetGps({
    device_mxid: gpsPanelOpen ? selectedMachine!.device_mxid : "",
    start_time: value.fromDate,
    end_time: value.toDate,
    max_points: 200,
  });
  const gps = useMemo(() => gpsData?.data ?? [], [gpsData]);
  const { basicCoordinates: gpsTrailCoords } = useMachineDetailMapCoordinates(
    selectedMachine ?? undefined,
    gps,
  );

  /**
   * The workflow writer. Replaces `useUpdateAlert`'s status PATCH on this page —
   * see `submitVerdict`. The legacy hook stays for other callers.
   */
  const verdictSubmitter = useAlertVerdict();

  // Clicking a timeline segment (non-edit mode) opens the on-map event detail
  // card for that specific event. Truck-cycle segments carry a TruckEvent cast
  // to MachineEvent (cycle UUID in `truckId`, no unified event detail), so
  // their row is flagged `isTruckCycle` and the card skips the image fetch.
  // No seek here — the click itself already positioned the timeline cursor.
  const handleTimelineEventOpen = useCallback(
    (event: MachineEvent, isTruckCycle: boolean) => {
      // Alert-track segments carry an Alert (timestamp, no startTime) — open
      // the alert card instead of the event card (whose date formatting would
      // throw on the missing startTime). The timeline is already on the right
      // machine and moment, so no snap/seek is needed.
      const asAlert = event as unknown as Alert;
      if (!isTruckCycle && (event as { startTime?: unknown }).startTime == null && asAlert.timestamp != null) {
        // Route through the shared alert-focus path so clicking an alert segment
        // behaves exactly like selecting the alert in the feed: activate the
        // alert's machine (guarded), seek the shared cursor + video to the alert
        // moment, and — in alert-review mode — open the docked video. Also pan +
        // zoom the map all the way in to the alert ("max"). Called via a ref so
        // this memoized handler keeps a stable identity for the timeline's track
        // memoization.
        focusAlertRef.current(asAlert, "max");
        return;
      }
      // The docked timeline only exists for the selected machine.
      const deviceMxid = selectedMachine?.device_mxid;
      if (!deviceMxid) return;
      const machineName = machineNameByDevice[deviceMxid] ?? deviceMxid;
      let row: MapEventRow;
      if (isTruckCycle) {
        const truck = event as unknown as TruckEvent;
        row = {
          id: truck.truckId,
          uuid: truck.truckId,
          deviceMxid,
          machineName,
          state: "Truck Cycle",
          startTime: new Date(event.startTime),
          durationSeconds: truck.cycleDuration,
          productive: truck.productivity > 0,
          color: event.color,
          isTruckCycle: true,
        };
      } else {
        // Truck-layer segments (truck-loaded, fragmentation, …) come through
        // this branch too but have eventId instead of uuid — fall back so the
        // row keeps a real identity (an undefined id breaks live-follow and
        // React keys). An empty uuid makes the card skip its image fetch.
        const uniqueId = event.uuid ?? (event as unknown as TruckEvent).eventId ?? event.id;
        row = {
          id: String(uniqueId ?? `${event.state}-${event.startTime}`),
          uuid: event.uuid ?? "",
          deviceMxid,
          machineName,
          state: String(event.state),
          startTime: new Date(event.startTime),
          durationSeconds: event.durationSeconds,
          productive: event.productive,
          color: event.color,
        };
      }
      setSelectedAlertId(null);
      setSelectedEvent(row);
      setOpenCategory(null); // the card takes the top-right slot
    },
    // Setters are stable (useState setters surfaced by useMapPanels), listed only
    // to satisfy exhaustive-deps.
    [selectedMachine?.device_mxid, machineNameByDevice, setOpenCategory],
  );

  // Sizes the shared MapView, which renders at a fixed pixel height.
  const { ref: mapAreaRef, height: mapHeight } = useMapAreaHeight();

  const {
    coordinates: basicCoordinates,
    newAlertIds,
    notice: mapNotice,
  } = useMapMarkers({
    view,
    locations,
    machines,
    alerts: locationAlerts,
    statusFilter: alertStatusFilter,
    selectedAlertId,
    gpsTrailCoords,
    isError,
    isLoading: isLoadingLocations || isLoadingMachines,
  });

  // The alert card is driven by the location alert list, so it works for an
  // alert clicked in the feed (any machine) or an on-map marker (its machine).
  // Timeline alert segments can reference alerts outside the feed (e.g. the
  // live rolling window) — fall back to the fetched detail so the card still opens.
  const selectedAlertIndex = locationAlerts.findIndex((a) => String(a.id) === selectedAlertId);
  const { data: alertDetail, isLoading: alertDetailLoading } = useGetAlertById(
    selectedAlertId ?? undefined,
    !!selectedAlertId,
  );
  const selectedAlert =
    selectedAlertIndex >= 0
      ? locationAlerts[selectedAlertIndex]
      : selectedAlertId != null && alertDetail?.data && String(alertDetail.data.id) === selectedAlertId
        ? alertDetail.data
        : null;

  /**
   * Canonical result of the open alert. The controls are built from THIS, never
   * from the list row: a row can be minutes old, and offering a verdict against
   * a stale one is how an operator overwrites a decision they never saw.
   * Refetched by the workflow mutation, which is also how the new status reaches
   * the screen — nothing here is applied optimistically.
   */
  const openAlertDetail = useAlertWorkspaceDetail(selectedAlert?.id ?? null);

  /**
   * What Prev/Next step through: the map feed UNION the bell's alerts.
   *
   * Not the feed alone. The feed obeys the global date picker while the bell is
   * fed from the Queue and the arrival probe, which do not — so on a narrow
   * review window the feed can hold fewer than two alerts and the card's
   * navigation disappeared entirely, even with plenty of live work to step
   * through. Both sets are real alerts; only one of them is range-bounded.
   */
  // Which machines get a timeline row. A hidden row is not collapsed — it is not
  // rendered at all, so its provider unmounts and its events / truck-events /
  // video-availability requests stop. That is what pays for polling the rows you
  // DO watch (see MapTimelineRow's INACTIVE_ROW_POLL_MS).
  const shownTimelineMxids = useMapTimelineVisibilityStore((s) => s.shownDeviceMxids);
  const toggleTimelineShown = useMapTimelineVisibilityStore((s) => s.toggleShown);
  const visibleTimelineRows = useMemo(
    () => visibleTimelineMachines(machines, shownTimelineMxids, selectedMachine?.device_mxid ?? null),
    [machines, shownTimelineMxids, selectedMachine],
  );

  const notifyItems = useAlertNotifyStore((s) => s.items);
  const alertNavList = useMemo(
    () => buildAlertNavList(locationAlerts, notifyItems),
    [locationAlerts, notifyItems],
  );
  const navAnchor = useMemo(
    () => navAnchorIndex(alertNavList, selectedAlertId, selectedAlert?.timestamp ?? null),
    [alertNavList, selectedAlertId, selectedAlert],
  );

  const alertImages = useMemo(() => alertDetail?.data?.images ?? [], [alertDetail]);
  const {
    index: imageIndex,
    setIndex: setImageIndex,
    lightboxOpen,
    setLightboxOpen,
    frameId: alertFrameId,
    step: stepImage,
  } = useAlertCarousel(alertImages, selectedAlertId);

  // Prefetch the prev/next alert's image detail while the card is open, so
  // stepping alerts shows the image instantly. Keys must match useGetAlertById
  // exactly ([QUERY_KEYS.ALERTS, <string id>]) for the cache hit to land.
  useEffect(() => {
    if (selectedAlertIndex < 0) return;
    for (const neighbor of [locationAlerts[selectedAlertIndex - 1], locationAlerts[selectedAlertIndex + 1]]) {
      if (!neighbor) continue;
      const id = String(neighbor.id);
      queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.ALERTS, id],
        queryFn: () => getAlertById(id),
        staleTime: 60_000,
      });
    }
  }, [selectedAlertIndex, locationAlerts, queryClient]);

  const hasSelectedLocation = view === "location" && !!selectedLocation;

  const mapSettings = {
    locationId: hasSelectedLocation ? selectedLocation?.id : undefined,
    overlayEnabled,
    onOverlayEnabledChange: setOverlayEnabled,
    overlayVersion,
    onOverlayVersionChange: setOverlayVersion,
    selectedPolygonIds,
    onSelectedPolygonIdsChange: setSelectedPolygonIds,
  };

  const handleSelectLocation = (id: number) => {
    if (!confirmActiveMachineSwitch(null)) return;
    setSelectedLocationId(id);
    setView("location");
    // Open the new location on a machine, so the switch lands on the same live
    // layout the page boots into rather than a blank one.
    setSelectedMachineId(defaultMachineIdAt(locations.find((l) => l.id === id)));
    setSelectedAlertId(null);
    setSelectedEvent(null);
    setTimelineOpen(false);
    setTimelineFloating(false);
    setVideoFloating(false);
    setCategoryFloating(false);
    setAlertSeekTime(null);
    // Reset overlay/polygon selection so the new location re-defaults.
    setOverlayVersion(undefined);
    setSelectedPolygonIds([]);
    setOpenCategory((cat) => (cat && CATEGORY_SCOPE[cat] === "machine" ? null : cat));
  };
  // Keep the breadcrumb bridge pointing at the current closure.
  selectLocationRef.current = handleSelectLocation;

  const handleBack = () => {
    if (!confirmActiveMachineSwitch(null)) return;
    setView("org");
    setSelectedMachineId(null);
    setSelectedAlertId(null);
    setSelectedEvent(null);
    setTimelineOpen(false);
    setTimelineFloating(false);
    setVideoFloating(false);
    setCategoryFloating(false);
    setAlertSeekTime(null);
    setOpenCategory((cat) => (cat && CATEGORY_SCOPE[cat] === "machine" ? null : cat));
  };

  // Changing the ACTIVE machine clears the (global) timeline edit queue in the
  // stack shell. Guard every user-initiated active-machine change with this
  // single helper so edits are never discarded silently. Returns true if the
  // switch may proceed, false to abort. Never prompts when the active machine
  // isn't actually changing (same device — e.g. selecting an alert/event that
  // belongs to the already-active machine) or when nothing is queued.
  /**
   * Whether the active machine can change without asking — same machine, or no
   * queued edits to lose. Split out so an AUTOMATIC action can take the same
   * decision without ever reaching the confirm() below.
   */
  const canSwitchActiveMachineSilently = (nextMachineId: number | null): boolean => {
    const currentDevice = selectedMachine?.device_mxid;
    const nextDevice =
      nextMachineId == null
        ? undefined
        : machines.find((m) => m.id === nextMachineId)?.device_mxid;
    if (nextDevice === currentDevice) return true;
    return !useTimelineEditQueueStore.getState().hasPendingChanges();
  };

  const confirmActiveMachineSwitch = (nextMachineId: number | null): boolean => {
    if (canSwitchActiveMachineSilently(nextMachineId)) return true;
    return window.confirm(t("page.confirmDiscardEdits"));
  };

  const handleSelectMachine = (id: number) => {
    const next = selectedMachineId === id ? null : id;
    // Both selecting a different machine and deselecting (→ null) change the
    // active machine, so guard both. Cancel aborts the whole action.
    if (!confirmActiveMachineSwitch(next)) return;
    setSelectedMachineId(next);
    setSelectedAlertId(null);
    if (next === null) {
      setTimelineOpen(false);
      setTimelineFloating(false);
      setVideoFloating(false);
      setCategoryFloating(false);
      setAlertSeekTime(null);
      setOpenCategory((cat) => (cat && CATEGORY_SCOPE[cat] === "machine" ? null : cat));
    }
  };

  // Timeline-row click in the stacked live preview: make that machine the
  // ACTIVE one (drives the video, on-map cards, GPS/map focus) WITHOUT
  // collapsing the stack — all timelines stay visible. Distinct from the rail's
  // toggle-to-deselect (`handleSelectMachine`). Guarded so an in-progress edit
  // isn't discarded silently; cancel keeps the current machine AND the edits.
  const handleActivateTimelineMachine = (id: number) => {
    if (id === selectedMachineId) return;
    if (!confirmActiveMachineSwitch(id)) return;
    setSelectedMachineId(id);
    setSelectedAlertId(null);
    setSelectedEvent(null);
    setAlertSeekTime(null);
    setAvailableTracks([]);
  };
  // Stable per-machine `onActivate` handlers so each memoized `MapTimelineRow`
  // keeps the same callback identity across the parent's 5s poll / cursor ticks
  // (an inline `() => handleActivateTimelineMachine(m.id)` would defeat the
  // row's React.memo). Cached by machine id; the ref keeps them calling the
  // latest closure without rebuilding the cache (same pattern as focusAlertRef).
  const activateMachineRef = useRef(handleActivateTimelineMachine);
  activateMachineRef.current = handleActivateTimelineMachine;
  const activateHandlersRef = useRef(new Map<number, () => void>());
  const getActivateHandler = useCallback((id: number) => {
    const cache = activateHandlersRef.current;
    let handler = cache.get(id);
    if (!handler) {
      handler = () => activateMachineRef.current(id);
      cache.set(id, handler);
    }
    return handler;
  }, []);

  const handleToggleCategory = (category: MapCategory) => {
    setSelectedAlertId(null); // the alert/event card and category share the top-right slot
    setSelectedEvent(null);
    if (category === "video") {
      // Video has its own docked/floating placement, not the category drawer.
      const shown = openCategory === "video" || videoFloating;
      setVideoFloating(false);
      setOpenCategory((prev) => (shown ? (prev === "video" ? null : prev) : "video"));
      return;
    }
    setCategoryFloating(false);
    setAlertSeekTime(null); // a freshly toggled category opens docked
    setOpenCategory((prev) => (prev === category ? null : category));
  };

  // Move the map camera to an alert. `hard` (feed) pans + zooms in; `max`
  // (timeline alert-segment click) pans + zooms all the way in; `soft` (arrow
  // stepping) pans and only zooms when the alert is off-screen. No coords → no
  // move (never crashes on a GPS-less alert).
  const requestAlertFocus = (alert: AlertFocusTarget, mode: MapFocusRequest["mode"]) => {
    const lat = alert.gps?.latitude;
    const lng = alert.gps?.longitude;
    if (lat == null || lng == null) return;
    focusNonceRef.current += 1;
    setFocusRequest({ lat, lng, mode, nonce: focusNonceRef.current });
  };

  // Shared "focus this alert" behavior, used by BOTH an Alerts-feed / marker /
  // step selection (via handleSelectAlert) and a timeline alert-segment click
  // (via handleTimelineEventOpen). Snaps to the alert's machine + moment so the
  // timeline (and video) track it — same snapping as location detail's alert
  // Prev/Next: activate the alert's machine (guarded), open the timeline, seek
  // the shared cursor + video to the alert moment, and — in alert-review mode —
  // surface the docked video. `focus` picks the map-camera behavior. Guards
  // BEFORE any state change so cancel aborts the whole action.
  const focusAlert = (
    alert: AlertFocusTarget,
    focus: "hard" | "soft" | "none" | "max",
    // A genuine user CLICK (feed row, map marker, bell item, timeline segment,
    // Prev/Next) acknowledges the alarm; the automatic auto-show (new-alert
    // auto-load, tab-return re-show) passes false so the alarm keeps looping
    // until the operator actually clicks the alert.
    acknowledgeAlarm = true,
  ) => {
    const nextMachine = machines.find((m) => m.device_mxid === alert.device_mxid);
    // `acknowledgeAlarm` doubles as "a human did this": only a real click may
    // put a confirm() in front of someone.
    //
    // An automatic show must never prompt. The guard exists so a CLICK cannot
    // silently discard queued timeline edits — but the new-alert auto-load and
    // the tab-return re-show are not clicks, and a window.confirm() fired by an
    // arriving alert is a blocking modal nobody asked for, freezing the page
    // until it is answered. So an automatic show that WOULD have prompted keeps
    // the current machine instead and still opens the card: the operator sees
    // the alert, and their unsaved edits are neither discarded nor interrupted.
    const isAutomatic = !acknowledgeAlarm;
    const targetMachineId = nextMachine ? nextMachine.id : selectedMachineId;
    let switchMachine = true;
    if (isAutomatic) {
      switchMachine = canSwitchActiveMachineSilently(targetMachineId);
    } else if (!confirmActiveMachineSwitch(targetMachineId)) {
      return;
    }
    setSelectedAlertId(String(alert.id));
    setSelectedEvent(null);
    // Showing an alert clears the bell badge (it's now on screen). The
    // repeat-until-acknowledged ALARM, though, only stops on a real click — an
    // automatic auto-show never silences it, so it keeps ringing like an alarm
    // until the operator engages. Runs after the switch guard, so a cancelled
    // machine switch never touches either.
    markAllAlertsSeen();
    if (acknowledgeAlarm) acknowledgeChime();
    // Reviewing an alert leaves live (the video seeks to it, the timeline centers
    // on it). Set it explicitly — not only via the seek's backward-scrub detector
    // — so the review-centered timeline domain engages immediately (deterministic,
    // no reliance on footage being known yet).
    setCursorLiveFollow(false);
    // A chosen moment — this is what pins the timelines, not the mere fact that
    // live-follow went off.
    setReviewing(true);
    const alertMs = alert.timestamp != null ? new Date(alert.timestamp).getTime() : null;
    // A LIVE alert (its moment is still inside the rolling live window) stays in
    // realtime — follow is now off, so the domain freezes centered on the alert
    // (reviewTime) and its footage is already fetched by the live queries. Only a
    // HISTORIC alert (older than the window, so absent from the live fetch) drops
    // to Range mode, which reloads the global date range so the moment exists.
    // The whole review VIEW has to be fetched, not just the alert instant — the
    // view is centered on the moment, so its left half is what falls outside.
    // See `alertFitsLiveWindow`.
    const inLiveWindow =
      timelineMode === "realtime" &&
      alertFitsLiveWindow(alertMs, realtimeWindow.fromDate.getTime(), liveSpanMs);
    if (!inLiveWindow) setTimelineMode("range");
    if (focus !== "none") requestAlertFocus(alert, focus);
    // The timeline opens so the snap is actually visible. `switchMachine` is
    // false only for an automatic show that would have had to prompt — the card
    // still opens, it just does not take the operator off what they were editing.
    if (nextMachine && switchMachine) setSelectedMachineId(nextMachine.id);
    setTimelineOpen(true);
    if (alertMs != null) setAlertSeekTime(alertMs);
    if (alertPreview) {
      // Alert-preview mode also surfaces the synced video alongside the timeline.
      setVideoFloating(false);
      setOpenCategory("video"); // docked video
    } else {
      setOpenCategory(null); // the card takes the top-right slot
    }
  };
  // Keep the ref pointing at the current closure so the memoized timeline handler
  // always calls the latest focusAlert without taking it as a dependency.
  focusAlertRef.current = focusAlert;

  // `focus` picks the camera behavior for this selection: "max" for a feed
  // click (pan + zoom all the way in to the alert's coords), "soft" for arrow
  // stepping (pan, zoom only if off-screen), "none" when the caller already
  // moved the camera (marker click centers directly in MapView with isAlert→20).
  const handleSelectAlert = (id: string, focus: "hard" | "soft" | "none" | "max" = "max") => {
    const alert = locationAlerts.find((a) => String(a.id) === id);
    if (!alert) {
      // Alert not in the current feed (e.g. resolved out of the window): still
      // guard the (no-op) switch, select it so the detail fallback renders the
      // card, and open nothing.
      if (!confirmActiveMachineSwitch(selectedMachineId)) return;
      setSelectedAlertId(id);
      setSelectedEvent(null);
      setOpenCategory(null);
      return;
    }
    focusAlert(alert, focus);
  };

  // Event row click — opens the on-map event card and snaps the timeline to the
  // event's machine + start time (same snapping as selecting an alert).
  const handleSelectEvent = (e: MapEventRow) => {
    const nextMachine = machines.find((m) => m.device_mxid === e.deviceMxid);
    // Guard BEFORE any state change so cancel aborts the whole action (no event
    // selection either). An event on the already-active machine never prompts.
    if (!confirmActiveMachineSwitch(nextMachine ? nextMachine.id : selectedMachineId)) return;
    setSelectedAlertId(null);
    setSelectedEvent(e);
    if (nextMachine) setSelectedMachineId(nextMachine.id);
    setTimelineOpen(true);
    setReviewing(true);
    setAlertSeekTime(e.startTime.getTime());
    setOpenCategory(null); // the card takes the top-right slot
  };

  // Alert card Prev/Next — step through the loaded alerts by *time* and snap the
  // timeline to each (via handleSelectAlert). `direction` is +1 for newer, -1 for
  // older; the feed is sorted newest-first, so newer is a lower index. Bounds-
  // checked so the ends are no-ops.
  const handleStepAlert = (direction: number) => {
    // An alert that holds a slot steps off it. One that does not (opened from a
    // timeline segment or the live window) sits BETWEEN two entries, so stepping
    // newer takes the entry above the anchor and stepping older takes the anchor
    // itself — neither slot belongs to it.
    const holdsSlot = alertNavList.some((a) => String(a.id) === selectedAlertId);
    const target = holdsSlot
      ? navAnchor - direction
      : direction > 0
        ? navAnchor - 1
        : navAnchor;
    const neighbor = alertNavList[target];
    if (!neighbor) return;
    // Focus directly rather than via handleSelectAlert: the neighbour may be an
    // alert the bell knows and the date-bounded feed does not, and that path
    // would drop it to "select the id, open nothing".
    // Soft focus: pan to the stepped alert, only zooming when it's off-screen.
    focusAlert(neighbor, "soft");
  };

  const handleMarkerSelect = (markerId: string) => {
    const sel = parseSelectionId(markerId);
    if (sel?.type === "location") handleSelectLocation(sel.id);
    else if (sel?.type === "machine") handleSelectMachine(sel.id);
    // Marker click already centers directly in MapView — don't double-move.
    else if (sel?.type === "alert") handleSelectAlert(sel.id, "none");
  };

  /**
   * Give the open alert a verdict through the WORKFLOW api.
   *
   * Was a bare `PATCH { status }` at a compatibility endpoint that carries no
   * workflow version: it allows a first decision and refuses to change a
   * completed one, so corrections quietly failed, and with no version two
   * operators could overwrite each other unseen (MIN-270). `useAlertVerdict`
   * reads canonical detail at submit time, sends `If-Match`, and picks the
   * command from the real current state.
   *
   * The status on screen is NOT updated here. It comes from the refetch the
   * mutation triggers, so what the operator reads is the server's answer rather
   * than this component's guess.
   */
  const submitVerdict = (verdict: AlertVerdict) => {
    if (!selectedAlert) return;
    // The "new" highlight is local decoration, not alert state — clearing it
    // early is safe even if the write is then rejected.
    markAlertHandled(selectedAlert.id);
    toast.promise(verdictSubmitter.submit(selectedAlert.id, verdict), {
      loading: t("page.verdict.loading"),
      success:
        verdict === "resolved"
          ? t("page.verdict.successTrue")
          : t("page.verdict.successFalse"),
      error: t("page.verdict.error"),
    });
  };

  // In alert-review while the timeline is live-following, the primary (left)
  // docked video must keep playing (muted autoplay) so the operator always sees
  // the realtime feed — without clicking "Go live". Gated to the map's
  // realtime/live path so the timeline editor's playback default is unchanged.
  const primaryLiveAutoplay = alertPreview && timelineMode === "realtime" && cursorLiveFollow;

  const handleToggleTimeline = () => {
    if (timelineOpen) {
      setTimelineOpen(false);
      setTimelineFloating(false);
    } else {
      setTimelineOpen(true);
      setAlertSeekTime(null);
    }
  };

  // Esc closes the topmost layer, one at a time: help → alert card → docked
  // category panel → video → timeline → machine selection → back to org.
  const handleEscape = () => {
    if (showShortcuts) return setShowShortcuts(false);
    if (selectedAlert) return setSelectedAlertId(null);
    if (selectedEvent) return handleCloseEventCard();
    if (openCategory && openCategory !== "video") {
      setOpenCategory(null);
      setCategoryFloating(false);
      setAlertSeekTime(null);
      return;
    }
    // Skipped while the video is permanently docked (alert review) — closing it
    // is a no-op there, so Esc must fall through to the next layer instead.
    if (videoOpen && !videoAlwaysDocked) return handleVideoClose();
    if (timelineOpen) {
      setTimelineOpen(false);
      setTimelineFloating(false);
      return;
    }
    if (selectedMachineId != null) return handleSelectMachine(selectedMachineId);
    if (view === "location") return handleBack();
  };

  useMapShortcuts({
    hasLocation: hasSelectedLocation,
    hasMachine: !!selectedMachine,
    alertCardOpen: !!selectedAlert,
    lightboxOpen: lightboxOpen && !!selectedAlert,
    onNextAlert: () => handleStepAlert(1),
    onPrevAlert: () => handleStepAlert(-1),
    verticalFrameStrip: reviewV2,
    onResolveAlert: () => submitVerdict("resolved"),
    onFalseAlert: () => submitVerdict("false"),
    onPrevImage: () => stepImage(-1),
    onNextImage: () => stepImage(1),
    onCloseLightbox: () => setLightboxOpen(false),
    onToggleCategory: handleToggleCategory,
    onToggleTimeline: handleToggleTimeline,
    onEscape: handleEscape,
    onToggleHelp: () => setShowShortcuts((s) => !s),
  });

  useSidebarCollapse();

  // Alert-review default layout: opening a machine immediately surfaces its
  // timeline + docked video (the live review layout) instead of leaving the
  // operator to toggle them. Fires once per active-machine change, so if the
  // operator closes either it stays closed until another machine is opened.
  useEffect(() => {
    if (!alertPreview || selectedMachineId == null) return;
    setTimelineOpen(true);
    setVideoFloating(false);
    setOpenCategory("video");
  }, [alertPreview, selectedMachineId, setTimelineOpen, setVideoFloating, setOpenCategory]);

  // The live event-card no longer auto-opens in realtime — the top-right Event
  // Details card is manual-open only (auto-showing the newest event cluttered the
  // live view). Events still open on click; liveLatestEvent still feeds the
  // live-edge calc above.

  /**
   * The newest genuinely-new alert, as the BELL knows it.
   *
   * Deliberately not `locationAlerts.filter(newAlertIds.has)`. The bell is fed
   * from the Queue plus the recent-arrival probe, neither bounded by the global
   * date picker, while the map feed is strictly inside it — so intersecting the
   * two silently dropped every arrival that fell outside the operator's chosen
   * window, and the map stopped opening new alerts at all.
   *
   * The feed row is preferred when there is one, because it carries the GPS the
   * camera move needs; otherwise the bell's own record is enough to open the
   * alert, and the card fetches the rest.
   */
  const newestNewAlert = useMemo(
    () => pickNewestNewAlert(notifyItems, newAlertIds, locationAlerts),
    [notifyItems, newAlertIds, locationAlerts],
  );

  // New-alert auto-load: whenever a genuinely-new alert exists (per the persisted
  // notify store — `newAlertIds` already excludes the backlog and any
  // resolved/aged-out alerts), load it exactly as if the operator clicked it —
  // activate its machine, pan the map, seek the timeline + video, open the
  // detail card — via the shared focusAlert path (which also marks the bell seen,
  // so the repeat-until-acknowledged chime stops the moment the alert is shown).
  // Because the notify store SURVIVES navigation, an alert that fired while the
  // operator was away is still "new" here — so landing on /map loads it too, and
  // the chime effectively "loops until the operator reaches the page". A newer
  // alert takes over (stay on the newest); `lastAutoLoadedRef` fires this once per
  // alert so it never re-yanks the operator off one they've since navigated away
  // from (that same alert stays the newest until it resolves/ages out).
  //
  // The operator can switch the whole behaviour off (`followNewAlerts`) while
  // validating previous days.
  const lastAutoLoadedRef = useRef<number | null>(null);
  useEffect(() => {
    if (alertsLoading) return;
    // Never auto-seek on a hidden tab: while backgrounded the video's rAF/seek
    // machinery is paused, so seeking there corrupts the live state. The
    // visibility handler below re-shows the pending alert once the tab is visible.
    if (document.hidden) return;
    const newest = newestNewAlert;
    if (!newest) return;
    if (lastAutoLoadedRef.current === newest.id) return;
    // Claim the alert even when not following, so switching following back on
    // does not retroactively jump to an arrival the operator sat out on purpose.
    // Only alerts that arrive AFTER they opt back in should move them.
    lastAutoLoadedRef.current = newest.id;
    if (!followNewAlerts) return;
    // Auto-show only — do NOT acknowledge the alarm (it keeps looping until the
    // operator actually clicks the alert).
    focusAlertRef.current(newest, "max", false);
  }, [newestNewAlert, alertsLoading, followNewAlerts]);

  // --- Resume the live view when the tab returns from the background ---
  // A hidden tab pauses rAF (the cursor loop stops) and throttles the polls, so
  // on return the cursor is behind the live edge. Re-ARM live-follow and let it
  // catch up — do NOT run the full "Go live" path.
  //
  // "Go live" sets `goLiveTime`, and `useVideoData` treats that as a deliberate
  // jump: it CLEARS every accumulated clip and refetches around wall-clock now.
  // Uploaded footage lags real time by minutes, so that asks for a moment with
  // no footage and leaves the tile with nothing to play — and it is the visible
  // "everything reloads" on every tab switch. Nothing needs resetting: React
  // Query resumes the interval polls when the tab becomes visible, and the
  // live-follow driver pulls the cursor to the footage edge as they land.
  //
  // A genuinely-new alert still takes priority (opened as if clicked); that seek
  // is deferred to here so it never runs on a hidden tab.
  const hiddenWasLiveRef = useRef(false);
  // Mirrored every render so the once-bound listener reads fresh values.
  const isLiveNowRef = useRef(false);
  isLiveNowRef.current = timelineMode === "realtime" && alertPreview && cursorLiveFollow;
  const liveSpanMsRef = useRef(liveSpanMs);
  liveSpanMsRef.current = liveSpanMs;
  const handleGoLiveRef = useRef(handleGoLive);
  handleGoLiveRef.current = handleGoLive;
  // Same candidate the auto-load uses — so an alert that arrived while the tab
  // was hidden opens on return even when it falls outside the date window.
  const pendingNewAlertRef = useRef<AlertFocusTarget | null>(null);
  pendingNewAlertRef.current = newestNewAlert;
  // Same switch, read through a ref: this listener is bound once.
  const followNewAlertsRef = useRef(followNewAlerts);
  followNewAlertsRef.current = followNewAlerts;
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        hiddenWasLiveRef.current = isLiveNowRef.current;
        return;
      }
      // Pull fresh timeline data IMMEDIATELY.
      //
      // A hidden tab pauses React Query's intervals, and `refetchOnWindowFocus`
      // is off app-wide (deliberately — it caused an alt-tab refetch burst), so
      // on return nothing fetches until each interval's next tick. Meanwhile the
      // live domain has already advanced with the wall clock. The window is
      // therefore showing a stretch of time the newest fetched event predates,
      // and the events bunch up against the left edge with empty space to their
      // right — badly so on a short live span, where five minutes away is the
      // whole window.
      //
      // Scoped to the live timeline's own queries rather than a blanket
      // refetch, which is exactly what the global setting exists to prevent.
      if (isLiveNowRef.current || hiddenWasLiveRef.current) {
        for (const key of [
          MACHINE_QUERY_KEYS.EVENTS,
          MACHINE_QUERY_KEYS.TRUCK_EVENTS,
          QUERY_KEYS.ALERTS,
        ]) {
          void queryClient.invalidateQueries({ queryKey: [key] });
        }
      }

      // Back in the tab: a genuinely-new alert takes priority (open it as if
      // clicked, which also acks the chime); otherwise re-anchor to live if we
      // were live when we left.
      // Not following: the arrival keeps its place in the bell and the operator
      // keeps theirs, so fall through to the ordinary re-anchor branch.
      const pending = followNewAlertsRef.current ? pendingNewAlertRef.current : null;
      if (pending && lastAutoLoadedRef.current !== pending.id) {
        lastAutoLoadedRef.current = pending.id;
        // Auto-show on return — not a click, so the alarm keeps looping.
        focusAlertRef.current(pending, "max", false);
      } else if (hiddenWasLiveRef.current) {
        // A hidden tab pauses rAF, so the video's playback cursor — which IS the
        // live position — stops while the clock does not. Come back after a few
        // minutes and it is minutes stale: the view keeps following the clock and
        // the cursor slides left, landing mid-timeline instead of at its anchor.
        //
        // Re-arming alone (what this used to do) moves nothing, so the stale
        // position simply persists. Re-syncing every time is the other extreme —
        // that re-fetches footage on every glance at another tab. So do it only
        // when the cursor has actually fallen past the view's own lead, i.e. when
        // it can no longer hold its anchored position.
        if (
          shouldResyncOnReturn(liveCursorRef.current, Date.now(), liveLeadMs(liveSpanMsRef.current))
        ) {
          handleGoLiveRef.current();
        } else {
          setCursorLiveFollow(true);
        }
      }
      hiddenWasLiveRef.current = false;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [setCursorLiveFollow, queryClient]);

  const handleCloseEventCard = () => {
    setSelectedEvent(null);
    if (timelineMode === "realtime" && timelineOpen) setLiveFollowPaused(true);
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      <div ref={mapAreaRef} className="relative flex-1 min-w-0">
        {mapHeight > 0 && (
          <MapView
            basicCoordinates={basicCoordinates}
            initialHeight={mapHeight}
            locationId={view === "location" ? selectedLocation?.id : undefined}
            // Keep the map tiles visible at all times — never the empty/loading takeover.
            isEmpty={false}
            // Map controls live in the right Settings panel, not floating on the map.
            hideLeftControls
            overlayEnabled={overlayEnabled}
            onOverlayEnabledChange={setOverlayEnabled}
            selectedOverlayVersion={overlayVersion}
            onOverlayVersionChange={setOverlayVersion}
            selectedPolygonIds={selectedPolygonIds}
            onSelectedPolygonIdsChange={setSelectedPolygonIds}
            onMarkerSelect={handleMarkerSelect}
            focusRequest={focusRequest}
            emphasizedMarkerId={emphasizedAlertId ? alertSelectionId(emphasizedAlertId) : null}
            onMarkerHover={(markerId, hovering) => {
              const sel = parseSelectionId(markerId);
              if (sel?.type !== "alert") return;
              setHoveredAlertId(hovering ? sel.id : null);
            }}
          />
        )}

        <MapModeBar
          mode={mapMode}
          onModeChange={setMapMode}
          collapsed={modeBarCollapsed}
          onToggleCollapsed={() => setModeBarCollapsed((c) => !c)}
          followNewAlerts={followNewAlerts}
          onToggleFollowNewAlerts={() => setFollowNewAlerts(!followNewAlerts)}
        />

        {mapNotice && (
          <div className="pointer-events-none absolute left-1/2 top-16 z-10 -translate-x-1/2">
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg border bg-background/95 px-3 py-2 text-sm shadow-md backdrop-blur">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              {mapNotice}
            </div>
          </div>
        )}

        {/* Floating machine cards over the left of the map. Hidden while an
            alert is under review: the review takes that corner for the camera
            panel, and picking a different machine mid-verdict is not a move the
            operator wants offered. */}
        {!(reviewV2 && selectedAlert) && (
        <MachineRail
          locationName={selectedLocation?.name ?? null}
          locations={locations}
          selectedLocationId={selectedLocationId}
          onSelectLocation={handleSelectLocation}
          machines={machines}
          kpisByDevice={kpisByDevice}
          selectedMachineId={selectedMachineId}
          showBack={view === "location"}
          // Not in V2: there is no full-height video column to clear there, so
          // the offset only pushed the rail off the corner it belongs in.
          offsetForVideo={videoDockedLeft && !reviewV2}
          onSelectMachine={handleSelectMachine}
          onBack={handleBack}
        />
        )}

        {openCategory && openCategory !== "video" && (
          <CategoryPanel
            category={openCategory}
            locationName={selectedLocation?.name ?? null}
            deviceMxid={selectedMachine?.device_mxid}
            machineName={selectedMachine?.name}
            machineColor={selectedMachine?.color}
            alerts={locationAlerts}
            machineNameByDevice={machineNameByDevice}
            alertsLoading={alertsLoading}
            selectedAlertId={selectedAlertId}
            onSelectAlert={(id) => handleSelectAlert(String(id))}
            alertStatus={alertStatusFilter}
            onAlertStatusChange={setAlertStatusFilter}
            alertFilters={alertFilters}
            onAlertFiltersChange={setAlertFilters}
            newAlertIds={newAlertIds}
            emphasizedAlertId={emphasizedAlertId}
            onHoverAlert={setHoveredAlertId}
            events={locationEvents}
            eventsLoading={eventsLoading}
            onSelectEvent={handleSelectEvent}
            gps={gps}
            gpsLoading={gpsPanelOpen && gpsStatus === "pending"}
            machines={machines}
            mapSettings={mapSettings}
            floating={categoryFloating}
            onToggleFloat={() => setCategoryFloating((f) => !f)}
            onClose={() => {
              setOpenCategory(null);
              setCategoryFloating(false);
              setAlertSeekTime(null);
            }}
          />
        )}

        {selectedEvent && !selectedAlert && (
          <MapMachineEventCard
            event={selectedEvent}
            shiftLeft={videoDockedRight}
            // Live events start frameless — keep polling the detail so the
            // card's image appears as soon as frames are processed.
            pollMs={timelineMode === "realtime" && timelineOpen ? TIMELINE_POLL_MS : undefined}
            onClose={handleCloseEventCard}
          />
        )}

        {/* Alert Review V2 — the redesigned review: one large frame over the map,
            the event's frames down the right edge, and the verdict as two
            oversized centred buttons. Replaces the floating card while an alert
            is open; the card still serves every other entry point. */}
        {reviewV2 && selectedAlert && (
          <AlertReviewV2
            alert={selectedAlert}
            images={alertImages}
            imageIndex={imageIndex}
            onImageIndexChange={setImageIndex}
            alertFrameId={alertFrameId}
            imagesLoading={alertDetailLoading}
            machineName={machineNameByDevice[selectedAlert.device_mxid] ?? selectedAlert.device_mxid}
            isUpdating={verdictSubmitter.isPending}
            onTrue={() => submitVerdict("resolved")}
            onFalse={() => submitVerdict("false")}
            currentStatus={openAlertDetail.data?.status ?? null}
            currentDisposition={openAlertDetail.data?.disposition ?? null}
            conflictMessage={
              verdictSubmitter.conflict?.alertId === selectedAlert.id
                ? verdictSubmitter.conflict.message
                : null
            }
            onDismissConflict={verdictSubmitter.dismissConflict}
            onPrev={() => handleStepAlert(-1)}
            onNext={() => handleStepAlert(1)}
            position={
              alertNavList.some((a) => String(a.id) === selectedAlertId)
                ? { index: alertNavList.length - 1 - navAnchor, total: alertNavList.length }
                : null
            }
            onClose={() => setSelectedAlertId(null)}
          />
        )}

        {!reviewV2 && selectedAlert && (
          <MapEventCard
            alert={selectedAlert}
            images={alertImages}
            imageIndex={imageIndex}
            onImageIndexChange={setImageIndex}
            alertFrameId={alertFrameId}
            imagesLoading={alertDetailLoading}
            lightboxOpen={lightboxOpen}
            onLightboxOpenChange={setLightboxOpen}
            machineName={machineNameByDevice[selectedAlert.device_mxid] ?? selectedAlert.device_mxid}
            // The list is newest-first, but the card numbers chronologically
            // (oldest = 1, like machine detail) so Prev counts down / Next up.
            // An alert with no slot of its own still navigates, anchored to where
            // it falls in time — it just doesn't claim a position label.
            index={alertNavList.length - 1 - navAnchor}
            total={alertNavList.length}
            inFeed={alertNavList.some((a) => String(a.id) === selectedAlertId)}
            isUpdating={verdictSubmitter.isPending}
            shiftLeft={videoDockedRight}
            onClose={() => setSelectedAlertId(null)}
            onPrev={() => handleStepAlert(-1)}
            onNext={() => handleStepAlert(1)}
            onResolve={() => submitVerdict("resolved")}
            onFalse={() => submitVerdict("false")}
            currentStatus={openAlertDetail.data?.status ?? null}
            currentDisposition={openAlertDetail.data?.disposition ?? null}
            conflictMessage={
              verdictSubmitter.conflict?.alertId === selectedAlert.id
                ? verdictSubmitter.conflict.message
                : null
            }
          />
        )}

        {/* Bottom timeline — toggle appears only when a machine is selected. */}
        {selectedMachine && !timelineOpen && (
          <Button
            size="sm"
            variant="secondary"
            className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 gap-2 shadow-md"
            onClick={() => {
              setTimelineOpen(true);
              setReviewing(false);
              setAlertSeekTime(null);
            }}
          >
            <ChevronUp className="h-4 w-4" />
            {t("page.timelineToggle")}
          </Button>
        )}

        {/* Live-preview timeline stack: one row per machine at the location.
            The ACTIVE machine's provider (this outer one) also hosts the synced
            video, so the active row and the video share one cursor. Inactive
            rows each get their own provider — click a row to make it active.
            Kept out of the MapView subtree so map rendering isn't tied to the
            playback cursor. */}
        {selectedMachine && (timelineOpen || videoOpen) && (
          <MachineTimelineProvider
            deviceMxid={selectedMachine.device_mxid}
            seekTime={alertSeekTime}
            timeWindow={timelineMode === "realtime" ? realtimeWindow : null}
            liveEdge={liveEdge}
            liveFootageStart={liveFootageStart}
            liveFollow={cursorLiveFollow}
            onLeaveLive={handleLeaveLive}
            cursorRef={liveCursorRef}
          >
            {/* ONE domain + playhead for every row in the stack. Must sit inside
                the shell provider — that is where the cursor the video drives
                lives — and outside the per-row providers, which re-declare the
                cursor context but leave this one alone. */}
            <LiveTimelineDomainProvider
              enabled={timelineMode === "realtime"}
              spanMs={liveSpanMs}
              paused={reviewTime != null}
              deviceMxid={selectedMachine.device_mxid}
            >
            {timelineOpen && (
              <MapTimelineStack
                activeDeviceMxid={selectedMachine.device_mxid}
                activeMachineName={selectedMachine.name}
                activeMachineColor={selectedMachine.color}
                machines={machines}
                shownDeviceMxids={shownTimelineMxids}
                onToggleMachineShown={toggleTimelineShown}
                // The newest footage the active tile actually holds — what the
                // live badge measures the cursor against.
                footageEndMs={liveEdge}
                machineCount={visibleTimelineRows.length}
                availableTracks={availableTracks}
                floating={timelineFloating}
                mode={timelineMode}
                onModeChange={setTimelineMode}
                liveSpanMs={liveSpanMs}
                onLiveSpanChange={setLiveSpanMs}
                liveFollow={cursorLiveFollow}
                reviewingAlert={!!selectedAlert && !cursorLiveFollow}
                // The whole MODE, not just the moment an alert is open: the
                // timeline should not rearrange itself under the operator the
                // instant they click an alert.
                compactLayout={reviewV2}
                onGoLive={handleGoLive}
                realtimeWindow={realtimeWindow}
                shiftLeft={videoDockedLeft}
                shiftRight={
                  videoDockedRight ||
                  (!!openCategory && openCategory !== "video" && !categoryFloating)
                }
                onToggleFloat={() => setTimelineFloating((f) => !f)}
                onClose={() => {
                  setTimelineOpen(false);
                  setTimelineFloating(false);
                }}
              >
                {visibleTimelineRows.map((m, index) => {
                  const isActive = m.id === selectedMachineId;
                  // Every row shares one domain (see LiveTimelineDomainProvider),
                  // so the axis renders only on the last VISIBLE row — a single
                  // axis aligns under every row's segments. Computed on the
                  // visible list, or hiding the bottom row takes the axis with it.
                  const isLast = index === visibleTimelineRows.length - 1;
                  // Every row is wrapped in its own provider with a STABLE
                  // element type — the active one forwards this ambient (active)
                  // provider (the one that also hosts the video) so its cursor
                  // stays synced with playback + edit state, inactive ones own an
                  // isolated provider. Because the element type never changes with
                  // `active`, switching the active machine re-renders rather than
                  // remounts every row (no timeline re-init / refetch flash).
                  return (
                    <MachineTimelineProvider
                      key={m.id}
                      deviceMxid={m.device_mxid}
                      active={isActive}
                      timeWindow={timelineMode === "realtime" ? realtimeWindow : null}
                    >
                      <MapTimelineRow
                        deviceMxid={m.device_mxid}
                        machineName={m.name}
                        // Only with a single row: the stack's left edge names
                        // the ACTIVE machine, which is no help telling several
                        // rows apart.
                        hideLabel={reviewV2 && visibleTimelineRows.length === 1}
                        machineColor={m.color}
                        active={isActive}
                        onActivate={isActive ? undefined : getActivateHandler(m.id)}
                        isRealtime={timelineMode === "realtime"}
                        realtimeWindow={realtimeWindow}
                        liveSpanMs={liveSpanMs}
                        reviewTime={reviewTime}
                        onEventOpen={isActive ? handleTimelineEventOpen : undefined}
                        onAvailableTracks={isActive ? setAvailableTracks : undefined}
                        onLiveLatestEvent={isActive ? setLiveLatestEvent : undefined}
                        showTimeAxis={isLast}
                      />
                    </MachineTimelineProvider>
                  );
                })}
              </MapTimelineStack>
            )}
            </LiveTimelineDomainProvider>
            {/* V2 with no alert open is just the map: locations and the timeline,
                the same as anywhere else. The camera belongs to the REVIEW, so
                it appears when there is something to review and not before. */}
            {videoOpen && !(reviewV2 && !selectedAlert) && (
              <MapVideoPanel
                deviceMxid={selectedMachine.device_mxid}
                machineName={selectedMachine.name}
                machineColor={selectedMachine.color}
                machineId={selectedMachine.id}
                locationId={selectedLocation?.id}
                machines={machines}
                activeMachineId={selectedMachineId}
                liveAutoplay={primaryLiveAutoplay}
                onFootageEdgeChange={handleFootageEdgeChange}
                jumpToLiveTime={goLiveTime}
                floating={videoFloating}
                dockSide={videoReviewDock ? "left" : "right"}
                reviewLayout={reviewV2 && !!selectedAlert}
                onToggleFloat={videoFloating ? handleVideoDock : handleVideoPopOut}
                hideClose={videoAlwaysDocked}
                onClose={handleVideoClose}
              />
            )}
          </MachineTimelineProvider>
        )}

        {showShortcuts && <MapShortcutsHelp onClose={() => setShowShortcuts(false)} />}
      </div>

      <CategoryRail
        openCategory={openCategory}
        videoActive={videoOpen}
        alertsBadge={newAlertsCount}
        hasSelectedMachine={!!selectedMachine}
        hasSelectedLocation={hasSelectedLocation}
        onToggle={handleToggleCategory}
      />
    </div>
  );
};

export default MapPreviewPage;
